import { useEffect, useRef } from 'react';
import { useSelector } from 'react-redux';
import SessionService from '../../services/session_service';

/**
 * Component to handle session initialization when user authenticates
 * Similar to mobile app's automatic sendSessionData() call after login
 */
export default function SessionInitializer() {
  const { user, isLoading } = useSelector((state) => state.auth);
  const hasInitializedSession = useRef(false);
  const lastUserUid = useRef(null); // Track last user UID to detect user changes
  const initializationInProgress = useRef(false); // Prevent concurrent initializations

  useEffect(() => {
    const initializeSession = async () => {
      // Wait for authentication to complete
      if (isLoading) {
        return;
      }

      // Only initialize for authenticated users
      if (!user || user.isAnonymous) {
        return;
      }

      // Prevent multiple simultaneous initializations
      if (initializationInProgress.current) {
        console.log('Session initialization already in progress, skipping...');
        return;
      }

      // Check if this is a new user or if we haven't initialized yet
      const isNewUser = lastUserUid.current !== user.uid;
      
      if (hasInitializedSession.current && !isNewUser) {
        return;
      }

      try {
        console.log('Initializing session for authenticated user...', user.uid);
        initializationInProgress.current = true;
        await SessionService.sendLoginSession(user);
        hasInitializedSession.current = true;
        lastUserUid.current = user.uid;
        console.log('Session initialized successfully');
      } catch (error) {
        console.error('Failed to initialize session:', error);
        // Don't set hasInitializedSession to true so we can retry later
        hasInitializedSession.current = false;
      } finally {
        initializationInProgress.current = false;
      }
    };

    initializeSession();
  }, [user, isLoading]);

  // Reset the initialization flag when user changes (logout/login)
  useEffect(() => {
    if (!user || user.isAnonymous) {
      hasInitializedSession.current = false;
      lastUserUid.current = null;
    }
  }, [user]);

  // This component doesn't render anything
  return null;
}
